import { useEffect, useRef, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@finmin/ui';
import { useAuth } from '@finmin/auth';
import { useToast } from '@finmin/ui';
import { isApiError } from '@finmin/api-client';
import { useVerifySignup } from '@/api/auth';
import { sendFirebaseOtp, verifyFirebaseOtp } from '@/lib/firebase-phone-auth';
import { AuthLayout } from '@/features/auth/components/auth-layout';
import { StepIndicator } from '@/features/auth/components/step-indicator';
import { SignupDetailsStep } from '@/features/auth/components/signup-details-step';
import { OtpStep } from '@/features/auth/components/otp-step';
import { useOtpTimer } from '@/features/auth/hooks/use-otp-timer';
import { normalizePhone, maskPhone } from '@/features/auth/utils/phone';
import {
  signupDetailsSchema,
  signupOtpSchema,
  type SignupDetailsValues,
  type SignupOtpValues,
} from '@/features/auth/types';

// ── Route ────────────────────────────────────────────────────────────────────

/** Search params for /signup — supports referral deep links. */
export interface SignupSearch {
  /** Referral code carried by an invite link, e.g. /signup?ref=ABC123. */
  ref?: string;
}

export const Route = createFileRoute('/signup')({
  // Parse `?ref=` (canonical) plus the aliases people commonly paste into a
  // link. Normalise to the same shape the form expects (uppercase, ≤20 chars,
  // alphanumeric) and silently drop anything malformed so we never prefill
  // garbage. The backend stays the source of truth for validity.
  validateSearch: (search: Record<string, unknown>): SignupSearch => {
    const raw = search.ref ?? search.referral ?? search.code ?? search.referral_code;
    if (typeof raw !== 'string') return {};
    const code = raw.trim().toUpperCase().slice(0, 20);
    return /^[A-Z0-9]+$/.test(code) ? { ref: code } : {};
  },
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'authenticated') {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: SignupPage,
});

// ── Page component ────────────────────────────────────────────────────────────

type SignupStep = 'details' | 'otp';

function SignupPage() {
  const navigate = useNavigate();
  const { refresh, status } = useAuth();
  const { toast } = useToast();
  const otpTimer = useOtpTimer();

  // Referral code from an invite link (/signup?ref=ABC123) — already validated
  // + normalised by the route's validateSearch.
  const { ref: referralFromLink } = Route.useSearch();

  const [step, setStep] = useState<SignupStep>('details');
  const [savedDetails, setSavedDetails] = useState<SignupDetailsValues | null>(null);
  const [otpValue, setOtpValue] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [sendOtpError, setSendOtpError] = useState<string | null>(null);
  const isVerifyingRef = useRef(false);

  // ── RHF forms ──────────────────────────────────────────────────────────────

  const detailsForm = useForm<SignupDetailsValues>({
    resolver: zodResolver(signupDetailsSchema),
    defaultValues: {
      full_name: '',
      mobile_e164: '',
      email: '',
      // Prefill from the invite link when present; user can still edit/clear it.
      referral_code: referralFromLink ?? '',
    },
    mode: 'onBlur',
  });

  const otpForm = useForm<SignupOtpValues>({
    resolver: zodResolver(signupOtpSchema),
    defaultValues: { otp: '' },
  });

  // ── OTP via Firebase ───────────────────────────────────────────────────────

  async function firebaseSendOtp(phoneNumber: string) {
    if (isSendingOtp) return;
    setIsSendingOtp(true);
    setSendOtpError(null);
    try {
      await sendFirebaseOtp(phoneNumber);
      setOtpValue('');
      otpTimer.start();
      setStep('otp');
      toast.success('OTP sent', `Code sent to ${maskPhone(phoneNumber)}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send OTP';
      setSendOtpError(message);
      toast.error('Failed to send OTP', message);
    } finally {
      setIsSendingOtp(false);
    }
  }

  const verifySignup = useVerifySignup({
    onSuccess: async () => {
      toast.success('Account created!', 'Welcome to FinminOS.');
      // Only refresh auth state here. The navigation happens in the effect
      // below, which fires AFTER React commits the authenticated status into
      // the router context — so `_protected` can't read a stale
      // `unauthenticated` and bounce the new user back to /login.
      await refresh();
    },
    onError: (err) => {
      // 409 = the account already exists. This happens when a prior
      // /signup/verify already consumed this OTP and created the account (e.g.
      // a duplicate in-flight submit, or a retry after the first verify
      // succeeded). The signup didn't "fail" — the user just has an account
      // now. Send them to login with the backend's message rather than a
      // scary "verification failed" toast.
      if (isApiError(err) && err.status === 409) {
        toast.warning('Account already exists', err.message);
        void navigate({ to: '/login' });
        return;
      }
      setOtpValue('');
      toast.error('Verification failed', err.message);
    },
  });

  // Navigate in once auth flips to authenticated. Done in an effect (post-commit)
  // so the route-guard context is guaranteed fresh. New users with an incomplete
  // profile are then routed to /questionnaire by the _protected layout.
  useEffect(() => {
    if (status === 'authenticated') {
      void navigate({ to: '/dashboard' });
    }
  }, [status, navigate]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleDetailsSubmit(values: SignupDetailsValues) {
    const normalised: SignupDetailsValues = {
      ...values,
      mobile_e164: normalizePhone(values.mobile_e164),
      referral_code: values.referral_code?.trim().toUpperCase() || '',
    };
    setSavedDetails(normalised);
    void firebaseSendOtp(normalised.mobile_e164);
  }

  async function handleOtpSubmit(values: SignupOtpValues) {
    if (!savedDetails) return;
    if (isVerifyingRef.current || verifySignup.isPending) return;
    isVerifyingRef.current = true;
    try {
      const idToken = await verifyFirebaseOtp(values.otp);
      verifySignup.mutate({
        full_name: savedDetails.full_name,
        id_token: idToken,
        email: savedDetails.email || undefined,
        referral_code: savedDetails.referral_code || undefined,
      });
    } catch {
      setOtpValue('');
      toast.error('Verification failed', 'Invalid or expired OTP');
    } finally {
      isVerifyingRef.current = false;
    }
  }

  function handleResend() {
    if (!savedDetails) return;
    void firebaseSendOtp(savedDetails.mobile_e164);
  }

  function handleBack() {
    setStep('details');
    setOtpValue('');
    otpTimer.reset();
    otpForm.reset();
    verifySignup.reset();
    setSendOtpError(null);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <AuthLayout>
      <StepIndicator current={step === 'details' ? 0 : 1} total={2} />

      <div id="recaptcha-container" />

      {step === 'details' ? (
        <Form {...detailsForm}>
          <SignupDetailsStep
            isPending={isSendingOtp}
            apiError={sendOtpError}
            referralFromLink={referralFromLink ?? null}
            onSubmit={handleDetailsSubmit}
          />
        </Form>
      ) : (
        <Form {...otpForm}>
          <OtpStep
            mobile={savedDetails?.mobile_e164 ?? ''}
            otpValue={otpValue}
            setOtpValue={setOtpValue}
            isPending={verifySignup.isPending}
            apiError={verifySignup.error?.message ?? null}
            resendSecondsLeft={otpTimer.secondsLeft}
            isResending={isSendingOtp}
            submitLabel="Verify & Create account"
            onSubmit={handleOtpSubmit}
            onResend={handleResend}
            onBack={handleBack}
          />
        </Form>
      )}
    </AuthLayout>
  );
}
